<template>
  <BaseCard class="flex h-fit w-full flex-col gap-4 p-6">
    <!-- Card header -->
    <TextSubtitle>Automatische incasso</TextSubtitle>
    <LoadingWrapper :loading="loading && showLoading">
      <template v-if="mandate?.status === MANDATE_STATUS.SUCCESS">
        <MessageComponent in-card class="">
          <div>
            <p>Er is een automatische incasso actief voor:</p>
            <p>
              <span class="font-medium">{{ mandate.debtor_iban }}</span>
              t.n.v.
              <span class="font-medium">{{ mandate.debtor_account_name }}</span>
            </p>
          </div>
        </MessageComponent>
        <p v-if="!mandate.iban_matches" class="max-w-prose text-sm">
          <span class="font-bold">Let op:</span> het rekeningnummer voor de
          automatische incasso komt <span class="italic">niet</span> overeen met
          het (debiteuren)rekeningnummer voor dit portfolio. Neem voor het
          wijzigen van de automatische incasso contact op met
          <a
            class="decoration-primary-600 hover:text-primary-600 underline decoration-2"
            href="mailto:debiteuren@repowered.nl"
            @click="triggerEvent('mandate-status:email-click')"
          >
            debiteuren@repowered.nl</a
          >.
        </p>
      </template>
      <div
        v-else-if="
          mandate?.status === MANDATE_STATUS.NEW ||
          mandate?.status === MANDATE_STATUS.NONE
        "
      >
        <p class="mb-2">Er is geen automatische incasso actief.</p>
        <BaseButton
          color="primary"
          :loading="loading"
          @click="followUrl(false)"
        >
          Stel automatische incasso in
          <BaseIcon name="arrow-right" class="ml-1" />
        </BaseButton>
      </div>
      <MessageComponent
        v-else-if="
          mandate?.status === MANDATE_STATUS.PENDING ||
          mandate?.status === MANDATE_STATUS.OPEN
        "
        in-card
        color="finance"
      >
        De aanvraag voor een automatische incasso is in behandeling. Houd deze
        pagina in de gaten voor de actuele status.
      </MessageComponent>
      <MessageComponent
        v-else-if="
          mandate?.status === MANDATE_STATUS.ERROR ||
          mandate?.status === MANDATE_STATUS.UNKNOWN
        "
        in-card
        color="danger"
      >
        <p>
          Er is een probleem met de automatische incasso. Neem contact op met
          <a
            class="decoration-danger-600 hover:text-danger-600 underline decoration-2"
            href="mailto:debiteuren@repowered.nl"
            @click="triggerEvent('mandate-status:email-click')"
          >
            debiteuren@repowered.nl</a
          >.
        </p>
      </MessageComponent>
      <p v-else-if="forbidden" class="italic">Geen toegang.</p>
      <p v-else class="italic">Niet beschikbaar.</p>
    </LoadingWrapper>
  </BaseCard>
</template>

<script setup>
import { ref, watch } from "vue"
import { fetchApiJson } from "@/services/repowerAPIService.js"
import { triggerEvent } from "@/services/plausibleService.js"
import HTTP_STATUS from "@/helpers/httpStatus.js"
import useNotificationStore from "@/stores/notificationStore.js"
import useFilterStore from "@/stores/filterStore.js"
import {
  BaseButton,
  BaseIcon,
  BaseCard,
  TextSubtitle,
  LoadingWrapper,
} from "@repowerednl/ui-component-library"
import MessageComponent from "@/components/information/MessageComponent.vue"

const MANDATE_STATUS = {
  NONE: "none",
  NEW: "new",
  SUCCESS: "success",
  PENDING: "pending",
  OPEN: "open",
  ERROR: "error",
  UNKNOWN: "unknown",
}

const notificationStore = useNotificationStore()
const filterStore = useFilterStore()

const mandate = ref(null)
const loading = ref(false)
const showLoading = ref(true)
const loadFailed = ref(false)
const forbidden = ref(false)

function followUrl(inner) {
  triggerEvent("mandate-status:bluem-click")
  if (!filterStore.currentPortfolio) {
    return
  }
  if (mandate.value?.status === MANDATE_STATUS.NONE && !inner) {
    loadMandate(filterStore.currentPortfolio, true, () => {
      followUrl(true)
    })
  } else if (mandate.value?.status === MANDATE_STATUS.NEW) {
    window.open(mandate.value.transaction_url, "_blank")
  } else {
    // No URL to follow for other statuses
  }
}

function loadMandate(portfolio, createIfMissing, onSuccess) {
  loading.value = true
  loadFailed.value = false
  forbidden.value = false
  fetchApiJson(
    `/portfolios/${portfolio.id}/mandate-status/?create_if_missing=${!!createIfMissing}`,
  )
    .then((data) => {
      mandate.value = data.data
      onSuccess?.(data)
    })
    .catch((error) => {
      loadFailed.value = true
      if (error.code === HTTP_STATUS.FORBIDDEN) {
        forbidden.value = true
      } else {
        notificationStore.pushError(
          "Fout bij ophalen automatische incasso status",
          `Er is een fout opgetreden bij het ophalen van de automatische incasso status. Probeer het later opnieuw. (code: ${error.code})`,
          "load-mandate-error",
        )
      }
    })
    .finally(() => {
      loading.value = false
    })
}

watch(
  () => filterStore.currentPortfolio,
  (portfolio) => {
    mandate.value = null
    showLoading.value = true
    if (portfolio) {
      loadMandate(portfolio, false, () => {
        showLoading.value = false
      })
    }
  },
  { immediate: true },
)
</script>
